import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, CheckCheck, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatMessageInfo } from '../../utils/formatMessageInfo';
import Username, { getDisplayName } from '../Common/Username';

const MessageInfoModal = ({ isOpen, onClose, message, isGroupMessage = false, readStatus = null, groupMembers = [] }) => {
  const { t } = useTranslation('chat');
  if (!isOpen || !message) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-md"
            style={{ zIndex: 10000 }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 10000 }}
            onClick={onClose}
          >
            <div
              className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10 dark:border-white/10">
                <h2 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white">{t('messageInfoModal.title')}</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4 overflow-y-auto">
                {/* Gönderildi */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--color-bg-elevated)] dark:bg-[#161f31] flex items-center justify-center">
                    <Check className="w-4 h-4 text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white">{t('messageInfoModal.sent')}</p>
                    <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] mt-0.5">
                      {formatMessageInfo(message.timestamp)}
                    </p>
                  </div>
                </div>

                {/* Teslim edildi */}
                {message.delivered_at && (
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--color-bg-elevated)] dark:bg-[#161f31] flex items-center justify-center">
                      <CheckCheck className="w-4 h-4 text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white">{t('messageInfoModal.delivered')}</p>
                      <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] mt-0.5">
                        {formatMessageInfo(message.delivered_at)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Okundu - Direct message */}
                {!isGroupMessage && message.read_at && (
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <CheckCheck className="w-4 h-4 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white">{t('messageInfoModal.read')}</p>
                      <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] mt-0.5">
                        {formatMessageInfo(message.read_at)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Group message read status */}
                {isGroupMessage && readStatus && (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <Users className="w-4 h-4 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white">
                          {t('messageInfoModal.readCount', { read: readStatus.readCount, total: readStatus.totalMembers })}
                        </p>
                        <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] mt-0.5">
                          {readStatus.allRead ? t('messageInfoModal.allRead') : t('messageInfoModal.notAllRead', { count: readStatus.totalMembers - readStatus.readCount })}
                        </p>
                      </div>
                    </div>

                    {/* Readers list */}
                    {readStatus.readers && readStatus.readers.length > 0 && (
                      <div className="mt-3 pl-11">
                        <p className="text-xs font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">{t('messageInfoModal.readersLabel')}</p>
                        <div className="space-y-1">
                          {readStatus.readers.map((readerId) => {
                            const reader = groupMembers.find(m => m.id === readerId);
                            if (!reader) return null;
                            return (
                              <div key={readerId} className="text-xs text-[var(--color-text-secondary)] dark:text-[var(--color-text-muted)]">
                                • <Username user={reader} variant="subtle" />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Non-readers list */}
                    {readStatus.readers && readStatus.readers.length < readStatus.totalMembers && (
                      <div className="mt-3 pl-11">
                        <p className="text-xs font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">{t('messageInfoModal.nonReadersLabel')}</p>
                        <div className="space-y-1">
                          {groupMembers
                            .filter(member => !readStatus.readers?.includes(member.id))
                            .map((member) => (
                              <div key={member.id} className="text-xs">
                                • <Username user={member} variant="subtle" />
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MessageInfoModal;

